import {
  LOCATION_CACHE_SCHEMA,
  bboxContains,
  bboxIntersects,
  createCacheRecord,
  normalizeCoverageBbox,
  parseCacheRecord,
} from './locationStoreCore.mjs';

export const LOCATION_CACHE_DB_NAME = 'pfandpirat-location-cache';
export const LOCATION_CACHE_DB_VERSION = 2;
export const LOCATION_CACHE_STORE = 'public-location-cells';
export const HYBRID_LOOT_CACHE_STORE = 'public-hybrid-loot-cells';
export const HYBRID_LOOT_CACHE_SCHEMA = 1;

function getIndexedDb(indexedDb = globalThis?.indexedDB) {
  return indexedDb && typeof indexedDb.open === 'function' ? indexedDb : null;
}

export function openLocationCache(indexedDb = globalThis?.indexedDB) {
  const idb = getIndexedDb(indexedDb);
  if (!idb) return Promise.resolve(null);
  return new Promise((resolve, reject) => {
    const request = idb.open(LOCATION_CACHE_DB_NAME, LOCATION_CACHE_DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(LOCATION_CACHE_STORE)) db.createObjectStore(LOCATION_CACHE_STORE, { keyPath: 'key' });
      if (!db.objectStoreNames.contains(HYBRID_LOOT_CACHE_STORE)) db.createObjectStore(HYBRID_LOOT_CACHE_STORE, { keyPath: 'key' });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('IndexedDB konnte nicht geöffnet werden.'));
  });
}

function transact(db, mode, callback, storeName = LOCATION_CACHE_STORE) {
  if (!db) return Promise.resolve(null);
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, mode);
    const store = transaction.objectStore(storeName);
    let result = null;
    try { result = callback(store); } catch (error) { reject(error); return; }
    transaction.oncomplete = () => resolve(result?.result ?? result ?? null);
    transaction.onerror = () => reject(transaction.error || new Error('IndexedDB-Transaktion fehlgeschlagen.'));
    transaction.onabort = () => reject(transaction.error || new Error('IndexedDB-Transaktion wurde abgebrochen.'));
  });
}

function sanitizeHybridSpot(raw = {}) {
  const key = String(raw?.key || raw?.id || '');
  const latitude = Number(raw?.latitude ?? raw?.lat);
  const longitude = Number(raw?.longitude ?? raw?.lng);
  if (!/^osm:(node|way|relation):[1-9][0-9]{0,18}$/.test(key)) return null;
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude) || latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) return null;
  return {
    key,
    id: key,
    source: 'osm',
    sourceId: String(raw?.sourceId || key.slice(4)).slice(0, 64),
    osmType: String(raw?.osmType || key.split(':')[1]).slice(0, 16),
    osmId: String(raw?.osmId || key.split(':')[2]).slice(0, 20),
    type: String(raw?.type || '').slice(0, 64),
    title: String(raw?.title || '').slice(0, 255),
    latitude,
    longitude,
    categoryKey: String(raw?.categoryKey || '').slice(0, 64),
    categoryLabel: String(raw?.categoryLabel || '').slice(0, 128),
    modelType: String(raw?.modelType || '').slice(0, 64),
    lootable: Boolean(raw?.lootable),
    priority: Number(raw?.priority || 200),
    sourceLabel: 'OpenStreetMap',
  };
}

function createHybridLootCacheRecord({ bbox, spots, meta = {}, etag = '', updatedAt = Date.now() } = {}) {
  const coverage = normalizeCoverageBbox(bbox);
  if (!coverage) return null;
  const byKey = new Map();
  for (const raw of Array.isArray(spots) ? spots : []) {
    const spot = sanitizeHybridSpot(raw);
    if (spot) byKey.set(spot.key, spot);
  }
  return {
    schema: HYBRID_LOOT_CACHE_SCHEMA,
    key: coverage.key,
    bbox: coverage,
    spots: [...byKey.values()],
    meta: {
      source: 'OpenStreetMap',
      attribution: String(meta?.attribution || '© OpenStreetMap contributors').slice(0, 255),
      license: String(meta?.license || 'ODbL').slice(0, 64),
      schema: String(meta?.schema || '').slice(0, 32),
    },
    etag: String(etag || '').slice(0, 256),
    updatedAt: Number(updatedAt) || Date.now(),
  };
}

export async function readHybridLootCacheRecords(requestedBbox, indexedDb = globalThis?.indexedDB) {
  try {
    const db = await openLocationCache(indexedDb);
    if (!db) return [];
    const records = await transact(db, 'readonly', (store) => store.getAll(), HYBRID_LOOT_CACHE_STORE);
    db.close();
    return (Array.isArray(records) ? records : [])
      .filter((record) => Number(record?.schema) === HYBRID_LOOT_CACHE_SCHEMA)
      .map((record) => createHybridLootCacheRecord(record))
      .filter((record) => record && (bboxContains(record.bbox, requestedBbox) || bboxIntersects(record.bbox, requestedBbox)))
      .sort((left, right) => Number(right.updatedAt) - Number(left.updatedAt));
  } catch {
    return [];
  }
}

export async function writeHybridLootCacheRecord(value, indexedDb = globalThis?.indexedDB) {
  const record = createHybridLootCacheRecord(value);
  if (!record) return false;
  try {
    const db = await openLocationCache(indexedDb);
    if (!db) return false;
    await transact(db, 'readwrite', (store) => store.put(record), HYBRID_LOOT_CACHE_STORE);
    db.close();
    return true;
  } catch {
    return false;
  }
}

export async function readLocationCacheRecords(requestedBbox, indexedDb = globalThis?.indexedDB) {
  try {
    const db = await openLocationCache(indexedDb);
    if (!db) return [];
    const records = await transact(db, 'readonly', (store) => store.getAll());
    db.close();
    return (Array.isArray(records) ? records : [])
      .map(parseCacheRecord)
      .filter((record) => record && (bboxContains(record.bbox, requestedBbox) || bboxIntersects(record.bbox, requestedBbox)))
      .sort((left, right) => Number(right.updatedAt) - Number(left.updatedAt));
  } catch {
    return [];
  }
}

export async function writeLocationCacheRecord(value, indexedDb = globalThis?.indexedDB) {
  const record = createCacheRecord(value);
  if (!record) return false;
  try {
    const db = await openLocationCache(indexedDb);
    if (!db) return false;
    await transact(db, 'readwrite', (store) => store.put(record));
    db.close();
    return true;
  } catch {
    return false;
  }
}

export async function clearPfandpiratLocationCache(indexedDb = globalThis?.indexedDB) {
  try {
    const db = await openLocationCache(indexedDb);
    if (!db) return false;
    await transact(db, 'readwrite', (store) => store.clear());
    db.close();
    return true;
  } catch {
    return false;
  }
}

export function locationCacheSchema() {
  return LOCATION_CACHE_SCHEMA;
}
